import { Fragment, useRef, useEffect } from 'react';
import type { Ref } from 'react';
import type { Table } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import type { DynamicTableColumnConfig } from './dynamic-table-types';
import type { CSSProperties } from 'react';
import { EmptyStatePanel } from '@/components/common/EmptyStatePanel';

type ColumnMeta = {
  className?: string;
  widthMode?: 'fit' | 'percent';
  widthSize?: number;
  widthMinSize?: number;
  widthMaxSize?: number;
};

function getColumnWidthStyle(getSize: () => number, meta: ColumnMeta | undefined): CSSProperties {
  const isPercent = meta?.widthMode === 'percent';
  const style: CSSProperties = {};

  if (isPercent && typeof meta?.widthSize === 'number') {
    style.width = `${meta.widthSize}%`;
  } else if (typeof meta?.widthSize === 'number') {
    style.width = meta.widthSize;
  } else {
    const size = getSize();
    if (size) style.width = size;
  }

  if (typeof meta?.widthMinSize === 'number') {
    style.minWidth = isPercent ? `${meta.widthMinSize}%` : meta.widthMinSize;
  }
  if (typeof meta?.widthMaxSize === 'number') {
    style.maxWidth = isPercent ? `${meta.widthMaxSize}%` : meta.widthMaxSize;
  }

  return style;
}

function getSortIndicator(sorted: false | 'asc' | 'desc'): string {
  if (sorted === 'asc') return ' ↑';
  if (sorted === 'desc') return ' ↓';
  return ' ⇅';
}

export interface DynamicTableListViewProps<T extends object> {
  table: Table<T>;
  columnsFeature: boolean;
  sortingFeature: boolean;
  rowSelectionFeature: boolean;
  expandableFeature: boolean;
  stripedRowsFeature: boolean;
  TH_CLASS: string;
  TD_CLASS: string;
  isLoading: boolean;
  loadingMessage: string;
  emptyMessage: string;
  colSpan: number;
  getRowRef?: (row: T, index: number) => Ref<HTMLTableRowElement> | undefined;
  onRowClick?: (row: T, index: number) => void;
  expandableContent?: (row: T, index: number) => React.ReactNode;
  columnConfigs?: DynamicTableColumnConfig<T>[]; // only for typing consistency
}

export function DynamicTableListView<T extends object>({
  table,
  columnsFeature,
  sortingFeature,
  rowSelectionFeature,
  expandableFeature,
  stripedRowsFeature,
  TH_CLASS,
  TD_CLASS,
  isLoading,
  loadingMessage,
  emptyMessage,
  colSpan,
  getRowRef,
  onRowClick,
  expandableContent,
}: DynamicTableListViewProps<T>) {
  const selectAllRef = useRef<HTMLInputElement>(null);
  const isSomeSelected = table.getIsSomePageRowsSelected();
  const isAllSelected = table.getIsAllPageRowsSelected();

  useEffect(() => {
    const el = selectAllRef.current;
    if (!el) return;
    el.indeterminate = isSomeSelected && !isAllSelected;
  }, [isSomeSelected, isAllSelected]);

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        {columnsFeature && (
          <thead className="bg-muted/50">
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {rowSelectionFeature && (
                  <th className={`${TH_CLASS} w-10 text-center`} scope="col" aria-label="Select all">
                    <input
                      ref={selectAllRef}
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={e => table.getToggleAllPageRowsSelectedHandler()(e)}
                      onClick={e => e.stopPropagation()}
                      className="rounded border-border"
                      aria-label="Select all rows on page"
                    />
                  </th>
                )}
                {expandableFeature && <th className={`${TH_CLASS} w-10`} aria-label="Expand" />}
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    className={`${TH_CLASS} ${((header.column.columnDef.meta as { className?: string }) ?? {}).className ?? ''} ${header.column.getCanSort() ? 'cursor-pointer select-none hover:bg-muted/80' : ''}`}
                    style={getColumnWidthStyle(
                      header.getSize.bind(header),
                      header.column.columnDef.meta as ColumnMeta
                    )}
                    onClick={
                      header.column.getCanSort()
                        ? header.column.getToggleSortingHandler()
                        : undefined
                    }
                  >
                    <div className="flex items-center gap-1">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {sortingFeature && header.column.getCanSort() && (
                        <span className="text-muted-foreground">
                          {getSortIndicator(header.column.getIsSorted())}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
        )}
        <tbody className="divide-y divide-border">
          {isLoading ? (
            <tr>
              <td colSpan={colSpan} className="h-24 text-center">
                <div className="flex justify-center items-center gap-2 text-muted-foreground">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="animate-spin"
                  >
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                  {loadingMessage}
                </div>
              </td>
            </tr>
          ) : table.getRowModel().rows.length === 0 ? (
            <tr>
              <td colSpan={colSpan} className="p-0">
                <EmptyStatePanel message={emptyMessage} />
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map(row => (
              <Fragment key={row.id}>
                <tr
                  ref={getRowRef?.(row.original, row.index)}
                  className={`hover:bg-muted/50 transition-colors ${
                    onRowClick ? 'cursor-pointer' : ''
                  } ${
                    // row.index is 0-based, so visual even rows are odd indexes (1,3,5,...)
                    stripedRowsFeature && row.index % 2 === 1 ? 'table-stripe-even' : ''
                  }`}
                  onClick={() => onRowClick?.(row.original, row.index)}
                >
                  {rowSelectionFeature && (
                    <td className={TD_CLASS}>
                      <input
                        type="checkbox"
                        checked={row.getIsSelected()}
                        onChange={e => row.toggleSelected(e.target.checked)}
                        onClick={e => e.stopPropagation()}
                        className="rounded border-border"
                      />
                    </td>
                  )}
                  {expandableFeature && expandableContent && (
                    <td className={TD_CLASS}>
                      <button
                        type="button"
                        onClick={e => {
                          e.stopPropagation();
                          row.toggleExpanded();
                        }}
                        className="p-1 rounded hover:bg-muted"
                        aria-expanded={row.getIsExpanded()}
                      >
                        {row.getIsExpanded() ? '▼' : '▶'}
                      </button>
                    </td>
                  )}
                  {row.getVisibleCells().map(cell => (
                    <td
                      key={cell.id}
                      className={`${TD_CLASS} ${((cell.column.columnDef.meta as { className?: string }) ?? {}).className ?? ''}`}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
                {expandableFeature && expandableContent && row.getIsExpanded() && (
                  <tr>
                    <td colSpan={colSpan} className="bg-muted/30 p-4">
                      {expandableContent(row.original, row.index)}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
